import { useEffect } from "react";
import { useMatch } from "react-router-dom";
import { APP_CONFIG } from "@/domains/global/config";
import { clientEnvModule } from "@/modules/client-env";
import { windowModule } from "@/modules/window";
import { localStorageModule } from "@/modules/local-storage";
import { shouldRedirectToExternalLandingPage } from "@/app/router/landing-page-redirect";
import { UTM_KEYS } from "./constants";

function promoteReferrerToUTMIfMissing(landingPageUrl: URL) {
  const originalReferrer = document.referrer;
  if (!originalReferrer) return;

  let refUrl: URL;
  try {
    refUrl = new URL(originalReferrer);
  } catch {
    return;
  }

  const isSelfRef = refUrl.origin === new URL("https://mem.ai").origin;
  if (isSelfRef) return;

  const host = refUrl.hostname.replace(/^www\./, "");
  if (!host) return;

  if (!landingPageUrl.searchParams.has("utm_source")) {
    landingPageUrl.searchParams.set("utm_source", host);
  }
  if (!landingPageUrl.searchParams.has("utm_medium")) {
    landingPageUrl.searchParams.set("utm_medium", "referral");
  }
}

/**
 * If the following criteria is true:
 * - User is in the main production environment (mem.ai)
 * - User is on the root page (https://mem.ai/)
 * - User has never logged in before (they are a new user to the platform)
 *
 * Then we redirect them to the `LandingPageUrl`.
 *
 * The goal here is that we would like to give new users some more information
 * about mem (and our landing page has a little more information about the product.)
 *
 * By default, we disable this in dev mode (because the assumption is that the user
 * will always want to hit the login page, and not be redirected to the landing page.)
 */
export const useRedirectToLandingPage = () => {
  const isRootPage = useMatch({
    path: "/",
    end: true,
  });

  const isLoginPage = useMatch("/login/*");

  useEffect(() => {
    if (clientEnvModule.isDesktop()) return;

    if (isLoginPage) {
      return localStorageModule.recordVisitingLoginPage();
    }

    const shouldRedirect = shouldRedirectToExternalLandingPage({
      hasPreviouslyVisitedLoginPage: localStorageModule.hasPreviouslyVisitedLoginPage(),
      hostname: windowModule.getLocationHostname(),
      isDesktop: false,
      isRootPage: Boolean(isRootPage),
      nodeEnv: clientEnvModule.nodeEnv(),
    });
    if (!shouldRedirect) return;

    // Preserve all URL parameters when redirecting to get.mem.ai
    const currentParams = windowModule.currentUrlSearchParams();
    const landingPageUrl = new URL(APP_CONFIG.URLS.LANDING_PAGE);

    // Copy all search parameters from current URL to landing page URL
    currentParams.forEach((value, key) => {
      landingPageUrl.searchParams.set(key, value);
    });

    // If there are no explicit UTM campaign params, promote the original referrer
    // into UTM so downstream attribution works without changes on get.mem.ai.
    const hasAnyUtm = UTM_KEYS.some(k => landingPageUrl.searchParams.has(k));
    if (!hasAnyUtm) {
      promoteReferrerToUTMIfMissing(landingPageUrl);
    }

    windowModule.navigateToExternal({ url: landingPageUrl });
  }, [isLoginPage, isRootPage]);
};
